import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { BaseRules, TextValues } from './illinois-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-illinois',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    DropdownModule,
    CommonModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    TruncateModule,
    TooltipModule,
  ],
  templateUrl: './illinois.component.html',
  styleUrl: './illinois.component.scss',
})
export class IllinoisComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  private readonly umpdPremiumPdfKey = 'premium_umpd_coveragemin_dropdown';

  getBodilyInjuryCoverageHeading(): string {
    return this.baseRules?.selectionId?.includes('StateMinimum') ||
      this.baseRules?.selectionId?.includes('PolicyLiabilityLimits')
      ? 'UM Bodily Injury Coverage:'
      : 'UM/UIM Bodily Injury Coverage:';
  }

  override setupHookForDropDownData(): void {
    super.setupHookForDropDownData();
    // Ensure readonly UMPD premium is repopulated on every state load/revisit.
    this.getReadonlyPremium();
  }

  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('illinois', form);
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('illinois', null);
    }
  }
  onDropdownSelect(control: any, event: any): void {
    if (event && this.isUmpdCoverageControl(control)) {
      this.setIllinoisUmpdPremium(event.premium ?? null);
      control.selectedPremium = event.premium ?? '';
    }
  }

  override handleDropdownSelection(control: any, event: any): void {
    super.handleDropdownSelection(control, event);

    if (!this.isUmpdCoverageControl(control)) {
      return;
    }

    const selectedPremium = event?.premium ?? control?.selectedPremium ?? null;
    this.setIllinoisUmpdPremium(selectedPremium);
  }

  override calculatePremium(c: any) {
    // Find the dropdown control for this baseRuleKey
    const lookupDropDownControl = this.controls.find(
      (control: any) => control.formControlName === c.formControlName,
    );
    if (!lookupDropDownControl) return;

    // Get the selected value from the form control
    const selectedValue = this.reactiveForm.get(
      lookupDropDownControl.formControlName,
    )?.value;
    // Find the corresponding item in the data provider
    const selectedItem = lookupDropDownControl.data?.find(
      (item: any) => item.value === selectedValue,
    );
    const premiumKey = 'premium_umpd_coveragemin_dropdown';

    if (!this.baseRulesService.pdfDataDump['illinois']) {
      this.baseRulesService.pdfDataDump['illinois'] = {};
    }
    this.baseRulesService.pdfDataDump['illinois'] = {
      ...this.baseRulesService.pdfDataDump['illinois'],
      [premiumKey]: selectedItem?.premium,
    };

    return selectedItem?.premium;
  }

  getReadonlyPremium(): void {
    if (!this.baseRulesService.pdfDataDump['illinois']) {
      this.baseRulesService.pdfDataDump['illinois'] = {};
    }
    if (
      this.baseRules?.['UMPD_CoverageMin_DropDown'] &&
      (this.baseRules?.['UMPD_CoverageMin_DropDown'].includes('readonly') ||
        this.baseRules?.['UMPD_CoverageMin_DropDown'].includes('show'))
    ) {
      this.baseRulesService.pdfDataDump['illinois'] = {
        ...this.baseRulesService.pdfDataDump['illinois'],
        [this.umpdPremiumPdfKey]:
          this.controls.find(
            (control: any) =>
              control.formControlName === 'UMPD_CoverageMin_DropDown',
          )?.data[0]?.premium || null,
      };
    } else {
      this.baseRulesService.pdfDataDump['illinois'] = {
        ...this.baseRulesService.pdfDataDump['illinois'],
        [this.umpdPremiumPdfKey]: null,
      };
    }
  }

  private isUmpdCoverageControl(control: any): boolean {
    const controlName = control?.formControlName;
    return (
      controlName === 'UMPD_Coverage_DropDown' ||
      controlName === 'UMPD_CoverageMin_DropDown'
    );
  }

  private setIllinoisUmpdPremium(premium: string | null): void {
    if (!this.baseRulesService.pdfDataDump['illinois']) {
      this.baseRulesService.pdfDataDump['illinois'] = {};
    }

    this.baseRulesService.pdfDataDump['illinois'] = {
      ...this.baseRulesService.pdfDataDump['illinois'],
      [this.umpdPremiumPdfKey]: premium,
    };
  }

  override setDynamicRules(control: string): void {
    super.setDynamicRules(control);
    // Re-evaluate premium when baseRules changes
    this.getReadonlyPremium();
  }
}
